import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { format } from "date-fns";
import {
  CheckCircle2,
  Calendar,
  Clock,
  Mail,
  Phone,
  Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import "./BookingConfirmation.css";

const BookingConfirmation = ({ bookingData }) => {
  const confirmationRef = useRef(null);
  const successRef = useRef(null);
  const detailsRef = useRef(null);
  const nextStepsRef = useRef(null);
  const actionsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      // Success icon pop animation
      tl.from(successRef.current.querySelector(".booking-confirmation__icon"), {
        scale: 0,
        rotation: -180,
        duration: 0.6,
        ease: "back.out(1.7)",
      })
        .from(
          successRef.current.querySelector(".booking-confirmation__title"),
          {
            opacity: 0,
            y: 20,
            duration: 0.5,
          },
          "-=0.3",
        )
        .from(
          successRef.current.querySelector(".booking-confirmation__subtitle"),
          {
            opacity: 0,
            y: 20,
            duration: 0.5,
          },
          "-=0.3",
        )
        .from(
          detailsRef.current,
          {
            opacity: 0,
            y: 30,
            duration: 0.6,
            ease: "power3.out",
          },
          "-=0.2",
        )
        .from(
          nextStepsRef.current,
          {
            opacity: 0,
            y: 30,
            duration: 0.6,
            ease: "power3.out",
          },
          "-=0.3",
        )
        .from(
          actionsRef.current,
          {
            opacity: 0,
            y: 20,
            duration: 0.5,
            ease: "power3.out",
          },
          "-=0.3",
        );
    }, confirmationRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="booking-confirmation" ref={confirmationRef}>
      <div className="booking-confirmation__success" ref={successRef}>
        <div className="booking-confirmation__icon">
          <CheckCircle2 size={64} strokeWidth={1.5} />
        </div>
        <h2 className="booking-confirmation__title">Booking Confirmed!</h2>
        <p className="booking-confirmation__subtitle">
          We've sent a confirmation email to{" "}
          <strong>{bookingData.email}</strong>
        </p>
      </div>

      <div className="booking-confirmation__details" ref={detailsRef}>
        <h3 className="booking-confirmation__details-title">
          Appointment Details
        </h3>

        <div className="booking-confirmation__info">
          <div className="booking-confirmation__info-item">
            <div className="booking-confirmation__info-icon">
              <Calendar size={20} />
            </div>
            <div className="booking-confirmation__info-content">
              <div className="booking-confirmation__info-label">
                Date & Time
              </div>
              <div className="booking-confirmation__info-value">
                {format(bookingData.date, "EEEE, MMMM do, yyyy")} at{" "}
                {format(bookingData.time, "h:mm a")}
              </div>
            </div>
          </div>

          <div className="booking-confirmation__info-item">
            <div className="booking-confirmation__info-icon">
              <Clock size={20} />
            </div>
            <div className="booking-confirmation__info-content">
              <div className="booking-confirmation__info-label">Duration</div>
              <div className="booking-confirmation__info-value">
                {bookingData.duration} minutes
              </div>
            </div>
          </div>

          <div className="booking-confirmation__info-item">
            <div className="booking-confirmation__info-icon">
              <Building2 size={20} />
            </div>
            <div className="booking-confirmation__info-content">
              <div className="booking-confirmation__info-label">Clinic</div>
              <div className="booking-confirmation__info-value">
                {bookingData.clinicName}
              </div>
            </div>
          </div>

          <div className="booking-confirmation__info-item">
            <div className="booking-confirmation__info-icon">
              <Mail size={20} />
            </div>
            <div className="booking-confirmation__info-content">
              <div className="booking-confirmation__info-label">Email</div>
              <div className="booking-confirmation__info-value">
                {bookingData.email}
              </div>
            </div>
          </div>

          {bookingData.phone && (
            <div className="booking-confirmation__info-item">
              <div className="booking-confirmation__info-icon">
                <Phone size={20} />
              </div>
              <div className="booking-confirmation__info-content">
                <div className="booking-confirmation__info-label">Phone</div>
                <div className="booking-confirmation__info-value">
                  {bookingData.phone}
                </div>
              </div>
            </div>
          )}
        </div>

        {bookingData.services.length > 0 && (
          <div className="booking-confirmation__services">
            <div className="booking-confirmation__services-label">
              Services Interested In:
            </div>
            <div className="booking-confirmation__services-list">
              {bookingData.services.map((service, index) => (
                <span key={index} className="booking-confirmation__service-tag">
                  {service}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="booking-confirmation__next-steps" ref={nextStepsRef}>
        <h3 className="booking-confirmation__next-steps-title">
          What Happens Next?
        </h3>
        <ol className="booking-confirmation__steps-list">
          <li>You'll receive a calendar invitation via email</li>
          <li>We'll prepare a custom strategy for your clinic</li>
          <li>Join the call at the scheduled time (Zoom link in email)</li>
          <li>Walk away with a clear growth roadmap</li>
        </ol>
      </div>

      <div className="booking-confirmation__actions" ref={actionsRef}>
        <Link
          to="/"
          className="booking-confirmation__btn booking-confirmation__btn--primary"
        >
          Back to Home
        </Link>
        <Link
          to="/services"
          className="booking-confirmation__btn booking-confirmation__btn--secondary"
        >
          Explore Our Services
        </Link>
      </div>

      <p className="booking-confirmation__note">
        Need to reschedule? Reply to the confirmation email and we'll help you
        find a new time.
      </p>
    </div>
  );
};

export default BookingConfirmation;
